import { useCallback } from 'react';
import { TimekeeperService } from 'services/TimekeeperService';
import { FixedRuleBase } from 'repository/timekeeper/@types/FixedRuleModel';
import { SnackbarMessage } from 'notistack';

interface AlertMessage {
    title: string;
    message: SnackbarMessage;
    severity: 'error' | 'success' | 'warning' | 'info';
}

type SetLoading = (loading: boolean) => void;
type Callback = () => void;
type HandlerAlert = (message: AlertMessage) => void;

const timekeeperService = new TimekeeperService();

export const useFixedRuleActions = () => {
    const addFixedRule = useCallback(
        async ({
            setLoading,
            body,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            body: FixedRuleBase;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            try {
                await timekeeperService.fixed_rule.createFixedRule(body);
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const updateFixedRule = useCallback(
        async ({
            setLoading,
            fixedRuleId,
            body,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            fixedRuleId: number;
            body: FixedRuleBase;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            try {
                await timekeeperService.fixed_rule.updateFixedRule(fixedRuleId, body);
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const deleteFixedRules = useCallback(
        async ({
            setLoading,
            fixedRuleIds,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            fixedRuleIds: number[];
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            try {
                await Promise.all(fixedRuleIds.map((fixedRuleId) => timekeeperService.fixed_rule.deleteFixedRule(fixedRuleId)));
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    return {
        addFixedRule,
        updateFixedRule,
        deleteFixedRules,
    };
};
